import Cookies from 'js-cookie';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/nextjs';
import { APP_NAME, PRIMARY_CATEGORY, QUERY_KEYS } from '@libs/constants';
import type { ProductAttribute, ProductFragmentAttributes } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { generateTechnicalDataCategories } from '@libs/technical-data';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { ApiProductTechnicalDataResponsePayload } from '@schemas/api-product-technical-data';
import type { TechnicalData } from '@models/data';

export type TechnicalDataCategory = {
  name?: string | null | undefined;
  label?: string | null | undefined;
  attributes: TechnicalData[];
};

export type TechnicalDataQueryResult = {
  categories: TechnicalDataCategory[];
  chemicalAttributeNames: string[];
  technicalProductAttributes: ProductFragmentAttributes;
};

export const useTechnicalDataQuery = (locale: string) => {
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const primaryCategoryCode = useProductPageStore(state => state.primaryCategoryCode);
  const unitSystem = useProductPageStore(state => state.unitSystem);
  const isEchainPage = primaryCategoryCode === PRIMARY_CATEGORY.ECHAIN;

  return useQuery<TechnicalDataQueryResult>(
    [QUERY_KEYS.PRODUCT_TECHNICAL_DATA, currentProduct.id, locale],
    async (): Promise<TechnicalDataQueryResult> => {
      const technicalDataReferenceEntityId = getAttributeValue<string>(
        currentProduct.attributes?.webshop_technical_data_attributes as ProductAttribute<string>
      );

      const response = await fetch(`/_${APP_NAME}/api/product-technical-data`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': Cookies.get('XSRF-TOKEN') || '',
        }),
        body: JSON.stringify({
          productId: currentProduct.id,
          technicalDataReferenceEntityId,
          withTechnicalDrawings: isEchainPage,
          locale,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch technical data for ${currentProduct.id}`);
      }

      const {
        technicalData,
        technicalProductAttributes,
        chemicalAttributeNames,
        attributeDefinitions,
        colourCodeDefinitions,
        driveTechnologySystemDefinitions,
      } = (await response.json()) as ApiProductTechnicalDataResponsePayload;

      if (!technicalData || technicalData.length === 0 || !attributeDefinitions) {
        throw new Error(`Missing technical data for ${currentProduct.id}`);
      }

      const productWithAdditionalTechnicalAttributes = {
        ...currentProduct,
        attributes: { ...currentProduct.attributes, ...technicalProductAttributes },
      };

      return {
        categories: generateTechnicalDataCategories(
          unitSystem,
          productWithAdditionalTechnicalAttributes,
          technicalData,
          attributeDefinitions,
          colourCodeDefinitions,
          driveTechnologySystemDefinitions
        ),
        chemicalAttributeNames,
        technicalProductAttributes,
      };
    },
    {
      onError: error => {
        const errorObject = error as Record<string, string>;
        const message = errorObject?.message ? errorObject.message : JSON.stringify(error);

        Sentry.captureException(new Error(`Fetch product technical data failed: ${message}`));
      },
    }
  );
};
